import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import environment from '../config/environment';

class SignalRService {
    constructor() {
        this.connection = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 2000;
    }

    async startConnection() {
        try {
            // Get the base URL from environment config
            const baseUrl = environment.API_BASE_URL;// || 'https://localhost:44340/api';
            const hubUrl = `${baseUrl.replace('/api', '')}/jobStatusHub`;
            
            console.log('SignalR connecting to:', hubUrl);

            this.connection = new HubConnectionBuilder()
                .withUrl(hubUrl, {
                    withCredentials: true
                })
                .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) // Retry delays in milliseconds
                .configureLogging(LogLevel.Information)
                .build();

            // Set up event handlers
            this.setupEventHandlers();

            // Start the connection
            await this.connection.start();
            this.isConnected = true;
            this.reconnectAttempts = 0;
            console.log('SignalR Connected to JobStatusHub');

            return true;
        } catch (error) {
            console.error('SignalR Connection Error:', error);
            this.isConnected = false;
            return false;
        }
    }

    setupEventHandlers() {
        if (!this.connection) return;

        // Handle connection state changes
        this.connection.onreconnecting((error) => {
            console.log('SignalR Reconnecting:', error);
            this.isConnected = false;
        });

        this.connection.onreconnected((connectionId) => {
            console.log('SignalR Reconnected. ConnectionId:', connectionId);
            this.isConnected = true;
            this.reconnectAttempts = 0;
        });

        this.connection.onclose((error) => {
            console.log('SignalR Connection Closed:', error);
            this.isConnected = false;
            
            // Attempt to reconnect if not at max attempts
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
                this.reconnectAttempts++;
                console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
                setTimeout(() => this.startConnection(), this.reconnectDelay);
            }
        });

        // Handle job status changes
        this.connection.on('JobStatusChanged', (jobStatus) => {
            console.log('Job Status Changed via SignalR:', jobStatus);
            // Dispatch custom event that components can listen to
            window.dispatchEvent(new CustomEvent('jobStatusChanged', { detail: jobStatus }));
        });
    }

    async stopConnection() {
        try {
            if (this.connection) {
                await this.connection.stop();
                this.connection = null;
                this.isConnected = false;
                console.log('SignalR Connection Stopped');
            }
        } catch (error) {
            console.error('Error stopping SignalR connection:', error);
        }
    }

    isConnectionActive() {
        return this.connection && this.isConnected;
    }

    // Method to manually invoke hub methods if needed
    async invoke(methodName, ...args) {
        if (this.connection && this.isConnected) {
            try {
                return await this.connection.invoke(methodName, ...args);
            } catch (error) {
                console.error(`Error invoking ${methodName}:`, error);
                throw error;
            }
        } else {
            throw new Error('SignalR connection is not active');
        }
    }
}

// Create a singleton instance
const signalRService = new SignalRService();

export default signalRService;
